import { useNavigation } from '@react-navigation/native';
import moment from 'moment';
import Config from 'react-native-config';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { CometChat } from '@cometchat/chat-sdk-react-native';
import { View, StyleSheet, TouchableOpacity, Text, Linking } from 'react-native';
import colors from '../../theme/Colors';
import images from '../../theme/Images';
import { getScreenWidth } from '../../utils/LayoutUtility';
import FastImageView from '../../components/FastImageView';
import size from '../../theme/Fonts';
import { isBlank, isDesktop, isIOS, isPresent, isWeb, isWebAndroid, isWebIOS } from '../../utils/BooleanUtility';
import { navigateToScreen } from '../../utils/NavigationUtility';
import { AnalyticsManager, EventParameterKey, EventType } from '../../analytics';
import { PERMISSION_STORE } from '../../config/Constants';
import { getStoreRef } from '../../store/StoreUtility';
import NavigationService from '../../navigator/NavigationService';
import { setPhoneNumberModalInfo } from '../../actions/LoginActions';
import Utility, { checkPermissionStatic } from '../../utils/Utility';
import DebouncedTouchableWithoutFeedback from '../../components/shared/DebouncedTouchableWithoutFeedback';
import DynamicLinkManager from '../../utils/DynamicLinkManager';
import { scheduleChatsNotification } from '../Routines/RoutineNotificationUtility';
import { DOWNLOAD_APP_MODAL_TYPE, SCREEN_CONSTANTS } from '../../config/ScreenConstants';
import { ShowCountDown } from './ShowCountDown';
import { saveSelfieData } from '../../actions/SkinSelfieAnalysisActions';
import { fetchBotConversation, fetchMessageConversation } from '../../utils/ApiCallingUtility';
import { debounce } from '../../utils/LodashUtility';
import { jsonParser } from '../../utils/JsonUtility';
import RemoteConfig from '../../utils/RemoteConfig';
import { REMOTE_CONFIG_KEYS } from '../../config/Constants';

const JSONAPIDeserializer = require('jsonapi-serializer').Deserializer;

// Memoized components for better performance
const ConversationInfo = React.memo(({
  name = '',
  dermatName = '',
  description = '',
  startsAt = '',
  endsAt = '',
  isLiveRoutine = false,
  lastMessage = '',
  lastMessageSender = '',
  isArchived = false,
  unreadMessageCount,
  guid,
  isUpcomingRoutine,
}) => (
  <View style={styles.conversationInfoContainer}>
    <View pad>
      <Text numberOfLines={1} ellipsizeMode="tail" style={styles.conversationTitle}>
        {name}
      </Text>
      <View style={styles.subtitleContainer}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.conversationSubtitle}>
          By FOXY
        </Text>
        {isLiveRoutine && <LiveIndicator />}
      </View>
    </View>
    {!isArchived && (
      <ConversationSubtitle
        description={description}
        isLiveRoutine={isLiveRoutine}
        lastMessageSender={lastMessageSender}
        lastMessage={lastMessage}
        unreadMessageCount={unreadMessageCount}
        guid={guid}
      />
    )}
    {isPresent(startsAt) && (
      <ShowCountDown
        startsAt={startsAt}
        endsAt={endsAt}
        isUpcomingRoutine={isUpcomingRoutine}
        showLeftAlignContainer
        disbale7DaysCountdown
      />
    )}
    {isArchived && <ArchivedTag />}
  </View>
));

const ConversationSubtitle = React.memo(({
  lastMessageSender,
  lastMessage,
  isLiveRoutine,
  description,
  unreadMessageCount,
  guid,
}) => {
  if (!isLiveRoutine) return null;

  const UnreadMessagesCount = useCallback(() => {
    const unreadCount = unreadMessageCount[guid];
    if (!unreadCount) return null;

    return (
      <View style={styles.unreadMessagesCountContainer}>
        <Text style={styles.unreadMessagesCountText}>{unreadCount}</Text>
      </View>
    );
  }, [unreadMessageCount, guid]);

  const containerStyle = useMemo(() => 
    isPresent(description)
      ? [styles.conversationContainer, { marginTop: 0 }]
      : styles.conversationContainer,
    [description]
  );

  return (
    <View style={containerStyle}>
      {lastMessage ? (
        <>
          <Text numberOfLines={2} ellipsizeMode="tail" style={styles.lastMessage}>
            <Text ellipsizeMode="tail" style={styles.lastMessageSender}>
              {lastMessageSender}:{' '}
            </Text>
            {lastMessage}
          </Text>
          {isLiveRoutine && <UnreadMessagesCount />}
        </>
      ) : (
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.description}>
          {description}
        </Text>
      )}
    </View>
  );
});

const LiveIndicator = React.memo(() => (
  <View style={styles.liveIndicatorContainer}>
    <View style={styles.liveIndicator} />
    <Text style={styles.liveIndicatorText}>LIVE</Text>
  </View>
));

const ArchivedTag = React.memo(() => (
  <View style={styles.archivedTextContainer}>
    <Text style={styles.archivedText}>Archived</Text>
  </View>
));

const Avatar = React.memo(({ 
  icon, 
  isLiveRoutine, 
  handle, 
  navigation 
}) => {
  const avatarAction = useCallback(() => {
    if (isBlank(handle)) return;
    DynamicLinkManager.handleLinkWithInternalTrackingParams(handle, ({ route = '', slug = '', extra = {} }) => {
      navigateToScreen({
        navigation,
        type: 'navigate',
        screen: route,
        params: { slug, extra },
      });
    });
  }, [handle, navigation]);

  const containerStyle = useMemo(() => [
    styles.avatarContainer,
    isLiveRoutine && styles.liveAvatarContainer,
  ], [isLiveRoutine]);

  return (
    <View style={containerStyle}>
      <DebouncedTouchableWithoutFeedback onPress={avatarAction}>
        <FastImageView 
          source={{ uri: icon || images.empty_profile_photo }} 
          style={styles.conversationIcon} 
        />
      </DebouncedTouchableWithoutFeedback>
    </View>
  );
});

const DefaultSizeCometChatConversation = (props) => {
  const [conversationData, setConversationData] = useState({});
  const [unreadMessageCount, setUnreadMessageCount] = useState({});
  const [lastMessage, setLastMessage] = useState('');
  const [lastMessageTimeStamp, setLastMessageTimestamp] = useState(null);
  const [lastMessageSender, setLastMessageSender] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAppInstallPromptVisibleMobileView, setAppInstallPromptVisibleMobileView] = useState(false);
  
  const dispatch = useDispatch();
  const { 
    itemData = {}, 
    listData: { cardBackgroundColor = '' } = {}, 
    index = '' 
  } = props;
  
  const navigation = useNavigation();
  const state = getStoreRef()?.getState();
  const userSelfieData = useSelector(state => state.UserAccountInfo?.selfieAnalysis);
  const selfieImage = useSelector(state => state.UserAccountInfo?.saveSelfieImage?.selfie_url);
  const authToken = state?.UserAccountInfo?.authToken;

  // Deserialize conversation data
  useEffect(() => {
    try {
      new JSONAPIDeserializer({
        typeAsAttribute: false,
        pluralizeType: true,
        keyForAttribute: 'camelCase',
      })
        .deserialize(itemData)
        .then(setConversationData);
    } catch (error) {
      console.error('Error deserializing data:', error);
    }
  }, [itemData]);

  const {
    name = '',
    dermatName = '',
    conversationWith = {},
    conversationWith: { guid = '', description = '', icon = '' } = {},
    cometChatGroupsSchedules = [],
    ctaAction = '',
    status = '',
    nextStartsAt = '',
    nextEndsAt = '',
    botType = '',
    routine: { endsAt = '' } = {},
    surveyFormUrl = '',
  } = conversationData;
  const isLiveRoutine = status === 'active';
  const isArchivedRoutine = status === 'archived';
  const isUpcomingRoutine = status === 'upcoming';

  // Schedule chat notifications
  useEffect(() => {
    if (isPresent(cometChatGroupsSchedules)) {
      scheduleChatsNotification(cometChatGroupsSchedules);
    }
  }, [cometChatGroupsSchedules]);

  // Add focus listener to fetch unread count and last message
  useEffect(() => {
    const blurListener = navigation.addListener('focus', () => {
      fetchUnreadCount();
      fetchLastMessage();
    });
    return () => blurListener();
  }, [guid, navigation]);

  // Add message listener
  useEffect(() => {
    if (!guid) return;

    const messageListener = new CometChat.MessageListener({
      onTextMessageReceived: () => {
        fetchUnreadCount();
        fetchLastMessage();
      },
      onMediaMessageReceived: () => {
        fetchUnreadCount();
        fetchLastMessage();
      },
      onCustomMessageReceived: () => {
        fetchUnreadCount();
        fetchLastMessage();
      },
    });

    CometChat.addMessageListener(guid, messageListener);
    return () => CometChat.removeMessageListener(guid);
  }, [guid]);

  // Fetch unread count and last message when guid or conversationData changes
  useEffect(() => {
    fetchUnreadCount();
    fetchLastMessage();
  }, [guid, conversationData]);

  const fetchUnreadCount = useCallback(() => {
    if (!guid || !isLiveRoutine) return;
    CometChat.getUnreadMessageCountForGroup(guid).then(
      setUnreadMessageCount,
      error => console.error('Group details fetching failed with exception:', error)
    );
  }, [guid, isLiveRoutine]);

  const getFormattedLastMessage = useCallback((conversation) => {
    const {
      lastMessage: {
        customData: { text = '', productArray = '', question = '' } = {},
        type: messageType = '',
        message = '',
      } = {},
    } = conversation;

    const messageTypes = {
      custom_text: text,
      custom_product: productArray[0]?.name || '',
      video: '📹 Video',
      image: '📷 Photo',
      extension_poll: `📊 ${question}`,
    };

    return messageTypes[messageType] || message;
  }, []);

  const fetchLastMessage = useCallback(() => {
    if (!guid || !isLiveRoutine) return;
    CometChat.getConversation(guid, 'group').then(
      (conversation) => {
        const {
          lastMessage: {
            sender: { name: senderName = '' } = {},
            sentAt,
          } = {},
        } = conversation;
        
        const messageText = getFormattedLastMessage(conversation);
        setLastMessageTimestamp(sentAt);
        setLastMessage(messageText);
        setLastMessageSender(senderName);
      },
      error => console.error('Group details fetching failed with exception:', error)
    );
  }, [guid, isLiveRoutine, getFormattedLastMessage]);

  const handleSelfieNavigation = useCallback(() => {
    const uploadedSelfieStatus = isPresent(selfieImage) || isPresent(userSelfieData?.my_attributes_values);
    if (uploadedSelfieStatus) {
      checkOrNavigateToProfile();
    } else {
      dispatch(saveSelfieData({ uploadedSelfieStatus: '' }));
      checkPermissionStatic(PERMISSION_STORE.camera, true, (permissionStatus) => {
        if (permissionStatus === 'granted' || permissionStatus === 'authorized') {
          navigation.navigate('TakeSkinSelfie', {
            previousScreen: 'ForYou',
            chatCallBack: checkOrNavigateToProfile,
            surveyFormUrl,
          });
        } else if (permissionStatus === 'never_ask_again' || permissionStatus === 'denied') {
          Linking.openSettings();
        }
      });
    }
  }, [userSelfieData, dispatch, navigation]);

  const onTapSignUp = useCallback(() => {
    setIsLoading(false);
    const { 
      previousScreen = '', 
      item: { data: { id = '', attributes: { name: chatName = '' } } } 
    } = props;
    
    AnalyticsManager.logEvent(EventType.chat.SIGN_UP_BUTTON_CLICK, {
      [EventParameterKey.PREVIOUS_SCREEN]: previousScreen,
      [EventParameterKey.CHAT_ID]: id,
      [EventParameterKey.CHAT_NAME]: chatName,
    });
    if (isBlank(authToken)) {
      dispatch(setPhoneNumberModalInfo(Utility.getLoginModalInfo('HIPER_FOR_YOU')));
      NavigationService.renderOnboarding({
        furtherAction: handleSelfieNavigation,
      });
    } else {
      handleSelfieNavigation();
    }
  }, [authToken, dispatch, props, handleSelfieNavigation]);


  const navigateToMessageInbox = useCallback(
    async (type = 'navigate') => {
      const { botType = '' } = conversationData;
      try {
        // Validate navigation type
        const validNavigationTypes = ['navigate', 'push', 'replace']; // Add more as needed
        const navigationType = validNavigationTypes.includes(type) ? type : 'navigate';

        // Common navigation params
        const navigationParams = {
          item: null, // Will be set based on condition
          isArchived: isArchivedRoutine,
          cometChatGroupsSchedules,
          isUpcoming: isUpcomingRoutine,
          isBotConversationEnable: isPresent(botType),
        };
  
        // Check if conversationWith.uid exists
        if (isPresent(conversationWith?.uid)) {
          navigationParams.item = conversationData;
        } else {
          // Fetch bot conversation if no UID
          const botData = await new Promise((resolve, reject) => {
            fetchBotConversation(botType, (data) => resolve(data), (error) => reject(error));
          });
          navigationParams.item = botData;
        }
  
        // Perform navigation
        navigation[navigationType](SCREEN_CONSTANTS.MESSAGES, navigationParams);
      } catch (error) {
        console.error('Error navigating to message inbox:', error);
        // Optionally handle error (e.g., show toast, retry logic)
      }
    },
    [
      navigation,
      conversationWith,
      conversationData,
      isArchivedRoutine,
      cometChatGroupsSchedules,
      isUpcomingRoutine,
      fetchBotConversation, // Added missing dependency
    ]
  );

  const checkOrNavigateToProfile = useCallback(() => {
    const {
      profile: { name: profileName = '', email: profileEmail = '' },
    } = getStoreRef().getState().UserAccountInfo;
    if (isBlank(profileName) || isBlank(profileEmail)) {
      navigation.navigate('ProfileSelection', {
        navigateToMessageInbox,
      });
    } else {
      navigateToMessageInbox('push');
    }
  }, [navigation]);

  const ctaActions = useMemo(() => ({
    join_chat: onTapSignUp,
    joined: onTapSignUp,
  }), [onTapSignUp]);

  const containerStyle = useMemo(() => 
    [styles.listItemStyle, { backgroundColor: cardBackgroundColor || '#D5DEEF' }],
    [cardBackgroundColor]
  );
  
  const timeStampText = useMemo(() => {
    if (isArchivedRoutine) {
      return moment(endsAt).format('DD/MM/YYYY');
    }
    if (lastMessageTimeStamp) {
      return moment.unix(lastMessageTimeStamp).format('hh:mm A');
    }
    return '';
  }, [isArchivedRoutine, endsAt, lastMessageTimeStamp]);
  
  const unreadCount = unreadMessageCount[guid];
  const signup = ctaAction === 'join_chat';

  const onPressActionButton = () => {
    if (isDesktop()) {
      navigation.navigate(SCREEN_CONSTANTS.APP_INSTALL_PROMPT, {
        from: location.pathname,
      });
      return;
    }
    if (isWebIOS() || isWebAndroid()){
      setAppInstallPromptVisibleMobileView(true);
      return;
    }
    if (typeof ctaActions[ctaAction] === 'function') {
      setIsLoading(true);
      ctaActions[ctaAction]();
    }
  };

  if (isAppInstallPromptVisibleMobileView) {
    const data =
      jsonParser(
        RemoteConfig.getValue(
          REMOTE_CONFIG_KEYS.text_message_for_download_modal,
        ),
      ) || {};
    const { title = '', subtitle = '' } = data[DOWNLOAD_APP_MODAL_TYPE.skin_coach] || {};
    navigation.navigate(SCREEN_CONSTANTS.DOWNLOAD_APP_MODAL, {
      title: title,
      subtitle: subtitle,
      setVisibility: setAppInstallPromptVisibleMobileView,
    });
  }

  return (
    <View style={[styles.container, { backgroundColor: cardBackgroundColor || '#D5DEEF' }]}>
      <DebouncedTouchableWithoutFeedback onPress={onPressActionButton} style={containerStyle}>
        <Avatar 
          icon={icon} 
          isLiveRoutine={isLiveRoutine} 
          navigation={navigation} 
        />
        <ConversationInfo
          name={name}
          dermatName={dermatName}
          description={description}
          lastMessage={lastMessage}
          lastMessageSender={lastMessageSender}
          startsAt={nextStartsAt}
          endsAt={nextEndsAt}
          isLiveRoutine={isLiveRoutine}
          isArchived={isArchivedRoutine}
          unreadMessageCount={unreadMessageCount}
          guid={guid}
          isUpcomingRoutine={isUpcomingRoutine}
          signup={signup}
        />
        {isPresent(ctaAction) && ctaAction !== 'joined' && !isArchivedRoutine && (
          <TouchableOpacity
            style={styles.joinNowButton}
            onPress={onPressActionButton}
            disabled={ctaAction === 'joined'}
          >
            <Text style={styles.buttonText}>Sign Up</Text>
          </TouchableOpacity>
        )}
        <View style={styles.indicatorContainer}>
          <Text style={[styles.archivedDateText, unreadCount && { color: '#1f2070' }]}>
            {timeStampText}
          </Text>
        </View>
      </DebouncedTouchableWithoutFeedback>
      <View style={styles.divider} />
    </View>
  );
};

export default React.memo(DefaultSizeCometChatConversation);

const styles = StyleSheet.create({
  tabImageStyle: {
    height: 24,
    width: 24,
    resizeMode: 'contain',
  },
  toastStyle: {
    position: 'absolute',
    bottom: 100,
  },
  container: {
    flex: 1,
    backgroundColor: colors.background,
    marginTop: 4,
  },
  listItemStyle: {
    backgroundColor: colors.hiper_chat_container,
    marginHorizontal: -6,
    flexDirection: 'row',
    padding: 12,
    width: getScreenWidth(),
    alignSelf: 'center',
  },
  conversationIcon: {
    height: 68,
    width: 68,
    borderRadius: 35,
    borderWidth: 1,
    borderColor: colors.subtitle,
    alignSelf: 'center',
  },
  tailedCircle: {
    position: 'absolute',
    height: 57,
    width: 57,
    alignSelf: 'center',
    left: 9,
    top: 9,
  },
  flexContainer: {
    flex: 1,
  },
  conversationTitle: {
    fontFamily: 'Roboto-Bold',
    fontSize: 16,
    color: colors.black,
    width: getScreenWidth() - 164,
    paddingRight: 2,
  },
  conversationSubtitle: {
    fontFamily: 'Roboto-Medium',
    fontSize: 12,
    color: colors.black,
    lineHeight: 16,
    marginTop: 2,
    maxWidth: '75%',
  },
  conversationBody: {
    fontFamily: 'Roboto-Medium',
    fontSize: 12,
    color: colors.black,
  },
  conversationLastMessageSender: {
    fontFamily: 'Roboto-Bold',
    fontSize: 12,
    color: colors.subtitle,
  },
  conversationLastMessage: {
    fontFamily: 'Roboto-Regular',
    fontSize: 12,
    color: colors.subtitle,
  },
  liveIndicatorContainer: {
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.foxyRed,
    padding: 1,
    paddingHorizontal: 3,
    marginLeft: 4,
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  liveIndicator: {
    height: 3,
    width: 3,
    borderRadius: 3,
    marginRight: 2,
    backgroundColor: colors.foxyRed,
  },
  liveIndicatorText: {
    fontFamily: 'Roboto-Bold',
    color: colors.foxyRed,
    fontSize: 8,
  },
  indicatorContainer: {
    alignItems: 'center',
    position: 'absolute',
    right: 12,
    top: 12,
  },

  joinNowButton: {
    height: 20,
    width: 60,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1B1F6A',
    borderRadius: 10,
    padding: 2,
    paddingVertical: 1.5,
    top: 18,
  },

  buttonText: {
    fontSize: size.h5,
    color: colors.white,
    alignSelf: 'center',
    textAlign: 'center',
    fontFamily: 'Roboto-Medium',
  },

  liveContainer: {
    backgroundColor: colors.routine_card_pink,
    borderRadius: 2,
    paddingHorizontal: 12,
    paddingVertical: 2,
    marginLeft: 6,
    position: 'absolute',
    left: 4,
    top: 12,
  },
  liveText: {
    color: colors.white,
    fontSize: size.h4_5,
    fontFamily: 'Roboto-Medium',
  },
  countdownContainer: {
    flexDirection: 'row',
    paddingRight: 8,
    marginLeft: -6,
    paddingVertical: 2,
    alignItems: 'center',
  },
  countdownText: {
    color: colors.black,
    fontFamily: 'Roboto-Medium',
    fontSize: size.h4,
    paddingHorizontal: 6,
    alignSelf: 'center',
    marginTop: 4,
  },
  dateText: {
    color: colors.black,
    fontFamily: 'Roboto-Medium',
    fontSize: size.h4,
    alignSelf: 'center',
    height: 16,
    marginTop: 2,
  },
  digitStyle: {
    backgroundColor: colors.black,
    borderRadius: 4,
  },
  digitTxtStyle: {
    color: colors.white,
    fontSize: size.h5,
    fontFamily: 'Roboto-Regular',
  },
  timeLabelStyle: {
    color: colors.black,
    fontFamily: 'Roboto-Regular',
    fontSize: 9,
    marginTop: 0,
    marginLeft: 2.1,
  },
  separatorStyle: {
    color: colors.black,
    marginTop: 3,
    marginLeft: 1,
  },
  archivedTextContainer: {
    padding: 4,
    borderRadius: 4,
    backgroundColor: colors.grey,
    maxWidth: 50,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 4,
  },
  archivedText: {
    fontSize: 10,
    fontFamily: 'Roboto-Medium',
    color: colors.white,
  },
  archivedConversationTitle: {
    fontFamily: 'Roboto-Bold',
    fontSize: 16,
    color: colors.black,
    width: getScreenWidth() - 174,
    paddingRight: 2,
    lineHeight: 16,
  },
  archivedDateText: {
    fontSize: 12,
    fontFamily: 'Roboto-Regular',
    color: colors.darkGrey,
  },
  subContainer: {},
  divider: {
    marginLeft: 188,
    width: '100%',
    height: 1,
    backgroundColor: colors.lightGrey,
    alignSelf: 'center',
  },
  conversationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 6,
  },
  lastMessageSender: {
    fontFamily: 'Roboto-Bold',
    fontSize: 12,
    color: '#000',
  },
  lastMessage: {
    fontFamily: 'Roboto-Regular',
    fontSize: 12,
    color: '#555',
    width: getScreenWidth() - 132,
    // marginTop: 12,
  },
  description: {
    fontFamily: 'Roboto-Regular',
    fontSize: 12,
    color: '#555',
    width: getScreenWidth() - 164,
  },
  unreadMessagesCountContainer: {
    marginLeft: 8,
    backgroundColor: '#1B1F6A',
    borderRadius: 12,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  unreadMessagesCountText: {
    color: colors.white,
    fontSize: 10,
    fontFamily: 'Roboto-Medium',
  },
  conversationInfoContainer: {
    flex: 1,
  },
  subtitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 2,
  },
  avatarContainer: {
    height: 74,
    width: 74,
    borderRadius: 38,
    justifyContent: 'center',
    marginRight: 12,
    marginTop: -2,
  },
  liveAvatarContainer: {
    borderWidth: 1.2,
    borderColor: Config.THEME_COLOR,
    borderRadius: 38,
  },
});
